import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface DualCalendarDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onDateRangeSelect: (startDate: Date, endDate: Date) => void;
  onClearFilter?: () => void;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  triggerRef?: React.RefObject<HTMLButtonElement>;
}

const DualCalendarDropdown: React.FC<DualCalendarDropdownProps> = ({
  isOpen,
  onClose,
  onDateRangeSelect,
  onClearFilter,
  selectedStartDate,
  selectedEndDate,
  triggerRef
}) => {
  const [startDate, setStartDate] = useState<Date | null>(selectedStartDate || null);
  const [endDate, setEndDate] = useState<Date | null>(selectedEndDate || null);
  const [leftMonth, setLeftMonth] = useState(new Date());
  const [rightMonth, setRightMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStartDate(selectedStartDate || null);
      setEndDate(selectedEndDate || null);
    }
  }, [isOpen, selectedStartDate, selectedEndDate]);

  /** Position dropdown below trigger so it doesn't expand the header. */
  useEffect(() => {
    if (!isOpen || !triggerRef?.current) {
      setPosition(null);
      return;
    }
    const trigger = triggerRef.current;
    const rect = trigger.getBoundingClientRect();
    const padding = 8;
    const top = rect.bottom + padding;
    let left = rect.left;
    const dropdownWidth = 560;
    if (left + dropdownWidth > window.innerWidth - 16) {
      left = window.innerWidth - dropdownWidth - 16;
    }
    if (left < 16) left = 16;
    setPosition({ top, left });
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          triggerRef?.current && !triggerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getPreviousMonth = (isLeft: boolean) => {
    if (isLeft) {
      setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1));
    } else {
      setRightMonth(new Date(rightMonth.getFullYear(), rightMonth.getMonth() - 1, 1));
    }
  };

  const getNextMonth = (isLeft: boolean) => {
    if (isLeft) {
      setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1));
    } else {
      setRightMonth(new Date(rightMonth.getFullYear(), rightMonth.getMonth() + 1, 1));
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    if (startDate && endDate) {
      return date >= startDate && date <= endDate;
    }
    return (startDate && date.toDateString() === startDate.toDateString()) ||
           (endDate && date.toDateString() === endDate.toDateString());
  };

  const isStartDate = (date: Date) => {
    return startDate && date.toDateString() === startDate.toDateString();
  };

  const isEndDate = (date: Date) => {
    return endDate && date.toDateString() === endDate.toDateString();
  };

  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate!) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    }
  };

  const handleConfirm = () => {
    if (startDate && endDate) {
      onDateRangeSelect(startDate, endDate);
    }
    onClose();
  };

  const handleCancel = () => {
    setStartDate(selectedStartDate || null);
    setEndDate(selectedEndDate || null);
    onClose();
  };

  const handleClearFilter = () => {
    if (onClearFilter) {
      onClearFilter();
    }
    onClose();
  };

  const renderCalendar = (month: Date, isLeft: boolean) => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(month);
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 text-gray-300"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const isCurrentDay = isToday(date);
      const isDateSelected = isSelected(date);
      const isStart = isStartDate(date);
      const isEnd = isEndDate(date);
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(date)}
          className={`
            h-8 w-8 rounded-full text-xs font-medium transition-all
            ${isCurrentDay ? 'ring-1 ring-blue-500' : ''}
            ${isDateSelected ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}
            ${isStart ? 'bg-blue-600 text-white' : ''}
            ${isEnd ? 'bg-blue-600 text-white' : ''}
            ${isDateSelected && !isStart && !isEnd ? 'bg-blue-200' : ''}
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="w-64 min-w-64">
        {/* Month Header */}
        <div className="flex items-center justify-between mb-3">
          <button 
            onClick={() => getPreviousMonth(isLeft)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-sm font-semibold text-gray-900">
            {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button 
            onClick={() => getNextMonth(isLeft)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
            <div key={day} className="h-6 flex items-center justify-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };

  if (!isOpen || !position) return null;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-[calc(100vw-2rem)] overflow-x-auto overflow-y-auto max-h-[calc(100vh-8rem)]"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 9999,
      }}
    >
      {/* Title */}
      <div className="text-center mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Select custom date range</h3>
      </div>

      {/* Date Input Fields */}
      <div className="flex space-x-4 mb-4">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
          <div className="relative">
            <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <input
              type="text"
              value={startDate ? formatDate(startDate) : ''}
              readOnly
              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="YYYY-MM-DD"
            />
          </div>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
          <div className="relative">
            <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <input
              type="text"
              value={endDate ? formatDate(endDate) : ''}
              readOnly
              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="YYYY-MM-DD"
            />
          </div>
        </div>
      </div>

      {/* Dual Calendars */}
      <div className="flex space-x-4 mb-4 min-w-fit">
        {renderCalendar(leftMonth, true)}
        {renderCalendar(rightMonth, false)}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <div className="flex space-x-2">
          <button
            onClick={handleClearFilter}
            className="px-3 py-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            Show All
          </button>
          <button
            onClick={handleCancel}
            className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
        </div>
        <button
          onClick={handleConfirm}
          disabled={!startDate || !endDate}
          className="px-4 py-2 text-xs bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm
        </button>
      </div>
    </div>
  );

  return createPortal(dropdownContent, document.body);
};

export default DualCalendarDropdown;
